import ArrowUp16 from '@carbon/icons-react/es/arrow--up/16';
import Movement16 from '@carbon/icons-react/es/movement/16';
import { Tooltip } from 'antd';
import React, {
  Dispatch,
  FC,
  RefObject,
  SetStateAction,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  CreateBranchingDetailsProps,
  FormQuestion,
  FormTemplate,
} from 'api/form/types';
import { Button } from 'components/atoms/button';
import { FormHeader } from 'components/molecules/forms/form-fields/form-header';
import { FormHeaderID } from './constants';
import {
  FormDropArea,
  FormQuestionLoading,
  ItemSelectionHeaderWrapper,
} from './form-builder-components';
import { FormQuestionRenderer } from './form-question-renderer';
import { RearrangeQuestions } from './rearrange';

interface Props {
  formTemplate: FormTemplate;
  centerColWrapperRef: RefObject<HTMLDivElement> | null;
  isCenterColScrolled: boolean;
  selectedFormItem: number | FormQuestion | null;
  setSelectedFormItem: Dispatch<SetStateAction<null | FormQuestion | number>>;
  createBranchingDetails: null | CreateBranchingDetailsProps;
  setCreateBranchingDetails: Dispatch<
    SetStateAction<null | CreateBranchingDetailsProps>
  >;
  createNewQuestionLoading: boolean;
}
const FormPreviewWrapper = styled.div`
  padding: var(--spacing-08) var(--spacing-11) 300px;
  position: relative;
  max-width: 800px;
  margin: 0 auto;
`;
const RearrangeButton = styled(Button)`
  position: fixed;
  top: 150px;
  left: 330px;
  transform: rotate(90deg);
`;
const ScrollToTopButton = styled(Button)`
  position: fixed;
  bottom: 40px;
  left: 330px;
`;

export const FormBuilderPreview: FC<Props> = ({
  formTemplate,
  centerColWrapperRef,
  isCenterColScrolled,
  selectedFormItem,
  setSelectedFormItem,
  createBranchingDetails,
  setCreateBranchingDetails,
  createNewQuestionLoading,
}) => {
  const [showRearrangeModal, setRerrangeModalVisibility] = useState(false);

  return (
    <FormPreviewWrapper>
      <ItemSelectionHeaderWrapper
        isSelected={selectedFormItem === FormHeaderID}
        hasHeaderImage={formTemplate.formTheme.headerImage}
        onClick={() => {
          setSelectedFormItem(FormHeaderID);
        }}
      >
        <section>
          <FormHeader
            name={formTemplate.name}
            description={formTemplate.description}
            formTheme={formTemplate.formTheme}
          />
        </section>
      </ItemSelectionHeaderWrapper>
      {formTemplate.formQuestions.map((formQuestion) => {
        return (
          <FormQuestionRenderer
            centerColWrapperRef={centerColWrapperRef}
            key={formQuestion.questionId}
            formQuestion={formQuestion}
            templateId={formTemplate.templateId}
            selectedFormItem={selectedFormItem}
            setSelectedFormItem={setSelectedFormItem}
            createBranchingDetails={createBranchingDetails}
            setCreateBranchingDetails={setCreateBranchingDetails}
          />
        );
      })}
      {createNewQuestionLoading ? (
        <FormQuestionLoading centerColWrapperRef={centerColWrapperRef} />
      ) : (
        <FormDropArea
          centerColWrapperRef={centerColWrapperRef}
          templateId={formTemplate.templateId}
          setSelectedFormItem={setSelectedFormItem}
        />
      )}
      {isCenterColScrolled && (
        <Tooltip
          mouseEnterDelay={0.8}
          placement="left"
          title={<FormattedMessage id="form_templates.scroll_top" />}
        >
          <ScrollToTopButton
            icon={<ArrowUp16 />}
            onClick={() =>
              centerColWrapperRef?.current?.scrollTo({
                top: 0,
                behavior: 'smooth',
              })
            }
          />
        </Tooltip>
      )}
      <Tooltip
        mouseEnterDelay={0.8}
        placement="left"
        title={<FormattedMessage id="form_templates.rearrange_questions" />}
      >
        <RearrangeButton
          icon={<Movement16 />}
          onClick={() => setRerrangeModalVisibility(true)}
        />
      </Tooltip>
      <RearrangeQuestions
        templateId={formTemplate.templateId}
        visible={showRearrangeModal}
        onClose={() => setRerrangeModalVisibility(false)}
      />
    </FormPreviewWrapper>
  );
};
